/**
 * Families catalog — pure metadata, NO three/konva imports.
 *
 * A "family" is the parametric archetype (e.g. "sofa"). A "preset" is a named variant
 * (e.g. "L-sofa"). Both prop families and opening families live here so the core model
 * is built once. The actual rendering lives in colocated, render-specific files:
 *   - 2D footprints  → `families/footprints.tsx`     (Konva, Design bundle)
 *   - 3D clay meshes → `three/familyMeshes.tsx`       (R3F, lazy 3D chunk)
 *
 * Opening families (door/window/gate) share the same catalogue; the rendering adapters
 * distinguish them by domain (edge-anchored plan symbol vs free footprint).
 */

import type { ParamSchema, ParamValues } from '@/model/params'
import { resolveParams } from '@/model/params'

export type PropCategory = 'furniture' | 'bathroom' | 'kitchen' | 'vehicles'

// ---------------------------------------------------------------------------
// Prop families
// ---------------------------------------------------------------------------

export interface PropFamily {
  id: string
  category: PropCategory
  label: string
  params: ParamSchema
  /** Compute the bounding-box from resolved params (used by 2D ghost, 3D placement, snapping). */
  dimensions(p: ParamValues): { width: number; depth: number; height: number }
}

const PROP_FAMILY_LIST: PropFamily[] = [
  // ── Furniture ────────────────────────────────────────────────────────────

  {
    id: 'bed',
    category: 'furniture',
    label: 'Bed',
    params: [
      { key: 'size', type: 'enum', label: 'Size', default: 'double',
        options: [
          { value: 'single', label: 'Single (3×6.5 ft)' },
          { value: 'double', label: 'Double (5×6.5 ft)' },
          { value: 'queen',  label: 'Queen (5×6.75 ft)' },
          { value: 'king',   label: 'King (6.5×6.75 ft)' },
        ] },
      { key: 'bunk', type: 'bool', label: 'Bunk bed', default: false },
      { key: 'headboard', type: 'enum', label: 'Headboard', default: 'panel',
        options: [
          { value: 'none', label: 'None' },
          { value: 'panel', label: 'Panel' },
          { value: 'upholstered', label: 'Upholstered' },
        ] },
    ],
    dimensions(p) {
      const sizes: Record<string, [number, number]> = {
        single: [3, 6.5], double: [5, 6.5], queen: [5, 6.75], king: [6.5, 6.75],
      }
      const [w, d] = sizes[p.size as string] ?? [5, 6.5]
      return { width: w, depth: d, height: p.bunk ? 5.5 : 2 }
    },
  },

  {
    id: 'sofa',
    category: 'furniture',
    label: 'Sofa',
    params: [
      { key: 'shape', type: 'enum', label: 'Shape', default: 'straight',
        options: [
          { value: 'straight', label: 'Straight' },
          { value: 'L',        label: 'L-shape' },
          { value: 'U',        label: 'U-shape' },
        ] },
      { key: 'seatLength', type: 'number', label: 'Seat length (ft)', default: 6, min: 3, max: 16, step: 0.5, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Depth (ft)', default: 3, min: 2, max: 5, step: 0.5, unit: 'ft' },
    ],
    dimensions(p) {
      const sl = p.seatLength as number
      const d  = p.depth as number
      const shape = p.shape as string
      if (shape === 'L') return { width: sl, depth: sl * 0.65, height: 2.8 }
      if (shape === 'U') return { width: sl, depth: sl * 0.75, height: 2.8 }
      return { width: sl, depth: d, height: 2.8 }
    },
  },

  {
    id: 'chair',
    category: 'furniture',
    label: 'Chair',
    params: [
      { key: 'width', type: 'number', label: 'Width (ft)', default: 1.8, min: 1, max: 4, step: 0.25, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Depth (ft)', default: 1.8, min: 1, max: 4, step: 0.25, unit: 'ft' },
    ],
    dimensions(p) { return { width: p.width as number, depth: p.depth as number, height: 3 } },
  },

  {
    id: 'table',
    category: 'furniture',
    label: 'Table',
    params: [
      { key: 'width', type: 'number', label: 'Width (ft)', default: 4, min: 1, max: 12, step: 0.5, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Depth (ft)', default: 2.5, min: 1, max: 6, step: 0.5, unit: 'ft' },
    ],
    dimensions(p) { return { width: p.width as number, depth: p.depth as number, height: 2.5 } },
  },

  {
    id: 'wardrobe',
    category: 'furniture',
    label: 'Wardrobe',
    params: [
      { key: 'doors', type: 'enum', label: 'Doors', default: '2door',
        options: [
          { value: '2door', label: '2-door' },
          { value: '3door', label: '3-door' },
        ] },
      { key: 'width', type: 'number', label: 'Width (ft)', default: 4, min: 2, max: 12, step: 0.5, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Depth (ft)', default: 2, min: 1, max: 3, step: 0.25, unit: 'ft' },
    ],
    dimensions(p) { return { width: p.width as number, depth: p.depth as number, height: 7 } },
  },

  {
    id: 'tv',
    category: 'furniture',
    label: 'TV',
    params: [
      { key: 'diagonalIn', type: 'number', label: 'Diagonal (in)', default: 55, min: 24, max: 120, step: 1, unit: 'in' },
      { key: 'mount', type: 'enum', label: 'Mount', default: 'stand',
        options: [
          { value: 'stand', label: 'Stand' },
          { value: 'wall',  label: 'Wall-mounted' },
          { value: 'projector', label: 'Projector + screen' },
        ] },
    ],
    dimensions(p) {
      const diagIn = p.diagonalIn as number
      const mount  = p.mount as string
      if (mount === 'projector') return { width: diagIn * 0.065, depth: 0.25, height: 0.5 }
      // 16:9 screen: width ≈ diag * 0.872, in feet
      const w = (diagIn * 0.872) / 12
      const d = mount === 'wall' ? 0.3 : 1
      return { width: w, depth: d, height: 2.5 }
    },
  },

  // ── Bathroom ─────────────────────────────────────────────────────────────

  {
    id: 'toilet',
    category: 'bathroom',
    label: 'Toilet',
    params: [
      { key: 'width', type: 'number', label: 'Width (ft)', default: 1.5, min: 1, max: 2.5, step: 0.25, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Depth (ft)', default: 2.3, min: 1.5, max: 3, step: 0.25, unit: 'ft' },
    ],
    dimensions(p) { return { width: p.width as number, depth: p.depth as number, height: 2.5 } },
  },

  {
    id: 'sink',
    category: 'bathroom',
    label: 'Sink',
    params: [
      { key: 'size', type: 'enum', label: 'Size', default: 'standard',
        options: [
          { value: 'compact',  label: 'Compact (1.5×1.25 ft)' },
          { value: 'standard', label: 'Standard (2×1.5 ft)' },
          { value: 'large',    label: 'Large (2.5×1.75 ft)' },
          { value: 'garden',   label: 'Garden/utility (3×2 ft)' },
        ] },
    ],
    dimensions(p) {
      const sizes: Record<string, [number, number]> = {
        compact: [1.5, 1.25], standard: [2, 1.5], large: [2.5, 1.75], garden: [3, 2],
      }
      const [w, d] = sizes[p.size as string] ?? [2, 1.5]
      return { width: w, depth: d, height: 2.8 }
    },
  },

  {
    id: 'shower',
    category: 'bathroom',
    label: 'Shower',
    params: [
      { key: 'width', type: 'number', label: 'Width (ft)', default: 3, min: 2, max: 6, step: 0.5, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Depth (ft)', default: 3, min: 2, max: 6, step: 0.5, unit: 'ft' },
    ],
    dimensions(p) { return { width: p.width as number, depth: p.depth as number, height: 7 } },
  },

  // ── Kitchen ──────────────────────────────────────────────────────────────

  {
    id: 'kitchenCounter',
    category: 'kitchen',
    label: 'Counter',
    params: [
      { key: 'width', type: 'number', label: 'Width (ft)', default: 6, min: 2, max: 20, step: 0.5, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Depth (ft)', default: 2, min: 1.5, max: 3, step: 0.25, unit: 'ft' },
    ],
    dimensions(p) { return { width: p.width as number, depth: p.depth as number, height: 3 } },
  },

  // ── Vehicles ─────────────────────────────────────────────────────────────

  {
    id: 'car',
    category: 'vehicles',
    label: 'Car',
    params: [
      { key: 'style', type: 'enum', label: 'Style', default: 'hatchback',
        options: [
          { value: 'hatchback', label: 'Hatchback' },
          { value: 'suv',       label: 'SUV' },
        ] },
      { key: 'width', type: 'number', label: 'Width (ft)', default: 6, min: 4, max: 8, step: 0.5, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Length (ft)', default: 14, min: 10, max: 20, step: 0.5, unit: 'ft' },
    ],
    dimensions(p) {
      return {
        width: p.width as number,
        depth: p.depth as number,
        height: p.style === 'suv' ? 5.5 : 4.8,
      }
    },
  },

  {
    id: 'bike',
    category: 'vehicles',
    label: 'Bike',
    params: [
      { key: 'width', type: 'number', label: 'Width (ft)', default: 2.5, min: 1.5, max: 4, step: 0.25, unit: 'ft' },
      { key: 'depth', type: 'number', label: 'Length (ft)', default: 6, min: 4, max: 10, step: 0.5, unit: 'ft' },
    ],
    dimensions(p) { return { width: p.width as number, depth: p.depth as number, height: 4 } },
  },
]

export const PROP_FAMILIES: Record<string, PropFamily> = Object.fromEntries(
  PROP_FAMILY_LIST.map((f) => [f.id, f]),
)

const CATEGORY_LABELS: Record<PropCategory, string> = {
  furniture: 'Furniture',
  bathroom: 'Bathroom',
  kitchen: 'Kitchen',
  vehicles: 'Vehicles',
}

const PROP_CATEGORY_ORDER: PropCategory[] = ['furniture', 'bathroom', 'kitchen', 'vehicles']

export const PROP_FAMILY_GROUPS: { category: PropCategory; label: string; families: PropFamily[] }[] =
  PROP_CATEGORY_ORDER.map((cat) => ({
    category: cat,
    label: CATEGORY_LABELS[cat],
    families: PROP_FAMILY_LIST.filter((f) => f.category === cat),
  }))

// ---------------------------------------------------------------------------
// Opening families
// ---------------------------------------------------------------------------

export interface OpeningFamily {
  id: string
  label: string
  params: ParamSchema
}

const OPENING_FAMILY_LIST: OpeningFamily[] = [
  {
    id: 'door',
    label: 'Door',
    params: [
      { key: 'width',     type: 'number', label: 'Width (ft)',  default: 3, min: 1, max: 8, step: 0.5, unit: 'ft' },
      { key: 'height',    type: 'number', label: 'Height (ft)', default: 7, min: 4, max: 12, step: 0.5, unit: 'ft' },
      { key: 'sillHeight',type: 'number', label: 'Sill (ft)',   default: 0, min: 0, max: 1, step: 0.1, unit: 'ft' },
      { key: 'leaves',    type: 'enum',   label: 'Leaves',      default: '1',
        options: [{ value: '1', label: 'Single' }, { value: '2', label: 'Double' }] },
      { key: 'panel',     type: 'enum',   label: 'Panel',       default: 'panel',
        options: [
          { value: 'flush',   label: 'Flush' },
          { value: 'panel',   label: 'Panel' },
          { value: 'glazed',  label: 'Glazed' },
        ] },
      { key: 'swing',     type: 'enum',   label: 'Swing',       default: 'in',
        options: [{ value: 'in', label: 'In' }, { value: 'out', label: 'Out' }] },
      { key: 'hinge',     type: 'enum',   label: 'Hinge',       default: 'left',
        options: [{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }] },
    ],
  },
  {
    id: 'window',
    label: 'Window',
    params: [
      { key: 'width',      type: 'number', label: 'Width (ft)',  default: 4, min: 1, max: 12, step: 0.5, unit: 'ft' },
      { key: 'height',     type: 'number', label: 'Height (ft)', default: 4, min: 1, max: 8, step: 0.5, unit: 'ft' },
      { key: 'sillHeight', type: 'number', label: 'Sill (ft)',   default: 3, min: 0, max: 6, step: 0.5, unit: 'ft' },
      { key: 'style',      type: 'enum',   label: 'Style',       default: 'plain',
        options: [
          { value: 'plain', label: 'Plain' },
          { value: 'grid',  label: 'Grid panes' },
        ] },
    ],
  },
  {
    id: 'gate',
    label: 'Gate',
    params: [
      { key: 'width',      type: 'number', label: 'Width (ft)',  default: 10, min: 4, max: 20, step: 0.5, unit: 'ft' },
      { key: 'height',     type: 'number', label: 'Height (ft)', default: 5, min: 3, max: 10, step: 0.5, unit: 'ft' },
      { key: 'sillHeight', type: 'number', label: 'Sill (ft)',   default: 0, min: 0, max: 0, step: 0, unit: 'ft' },
      { key: 'kinematics', type: 'enum',   label: 'Kinematics',  default: 'sliding',
        options: [
          { value: 'swing',      label: 'Swing' },
          { value: 'sliding',    label: 'Sliding' },
          { value: 'biparting',  label: 'Bi-parting' },
        ] },
      { key: 'leaves',     type: 'enum',   label: 'Leaves',      default: '1',
        options: [{ value: '1', label: 'Single' }, { value: '2', label: 'Double' }] },
      { key: 'style',      type: 'enum',   label: 'Style',       default: 'simple',
        options: [
          { value: 'simple', label: 'Simple' },
          { value: 'grand',  label: 'Grand' },
        ] },
      { key: 'swing',      type: 'enum',   label: 'Swing',       default: 'in',
        options: [{ value: 'in', label: 'In' }, { value: 'out', label: 'Out' }] },
      { key: 'hinge',      type: 'enum',   label: 'Hinge',       default: 'left',
        options: [{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }] },
    ],
  },
]

export const OPENING_FAMILIES: Record<string, OpeningFamily> = Object.fromEntries(
  OPENING_FAMILY_LIST.map((f) => [f.id, f]),
)

// ---------------------------------------------------------------------------
// Convenience: resolve a prop family's params to get its dimensions
// ---------------------------------------------------------------------------

export function propDimensions(
  familyId: string,
  params: ParamValues,
): { width: number; depth: number; height: number } {
  const family = PROP_FAMILIES[familyId]
  if (!family) return { width: 2, depth: 2, height: 2 }
  return family.dimensions(resolveParams(family.params, params))
}
